import React, { useEffect, useState } from 'react';
import { Button, Card, DatePicker, Space, Table, Tag } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useSelfService } from '../../hooks/useSelfService';
import { AttendanceRecord, AttendanceStatus } from '../../types';

const { RangePicker } = DatePicker;

const STATUS_COLOR: Record<AttendanceStatus, string> = {
  PRESENT: 'green',
  ABSENT: 'red',
  HALF_DAY: 'gold',
  ON_LEAVE: 'blue',
  PUBLIC_HOLIDAY: 'purple',
};

const SelfServiceAttendanceView: React.FC = () => {
  const { attendance, loading, fetchMyAttendance } = useSelfService();
  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('month'),
    dayjs(),
  ]);

  useEffect(() => {
    fetchMyAttendance(range[0].format('YYYY-MM-DD'), range[1].format('YYYY-MM-DD'));
  }, []);

  const handleSearch = () => {
    fetchMyAttendance(range[0].format('YYYY-MM-DD'), range[1].format('YYYY-MM-DD'));
  };

  const columns = [
    { title: 'Date', dataIndex: 'attendanceDate', key: 'attendanceDate' },
    {
      title: 'Status', dataIndex: 'status', key: 'status',
      render: (s: AttendanceStatus) => <Tag color={STATUS_COLOR[s]}>{s?.replace('_', ' ')}</Tag>,
    },
    { title: 'Check In', dataIndex: 'checkIn', key: 'checkIn' },
    { title: 'Check Out', dataIndex: 'checkOut', key: 'checkOut' },
    {
      title: 'Hours Worked', dataIndex: 'hoursWorked', key: 'hoursWorked',
      render: (v: number) => v != null ? `${v.toFixed(1)}h` : '—',
    },
    { title: 'Remarks', dataIndex: 'remarks', key: 'remarks', ellipsis: true },
  ];

  const presentDays = attendance.filter((r) => r.status === 'PRESENT').length;
  const absentDays = attendance.filter((r) => r.status === 'ABSENT').length;
  const halfDays = attendance.filter((r) => r.status === 'HALF_DAY').length;

  return (
    <div className="p-6">
      <Card
        title="My Attendance"
        extra={
          <Space>
            <RangePicker
              value={range}
              onChange={(dates) => { if (dates?.[0] && dates?.[1]) setRange([dates[0], dates[1]]); }}
            />
            <Button type="primary" onClick={handleSearch}>Search</Button>
          </Space>
        }
      >
        {/* Summary row */}
        <div className="flex gap-6 mb-4 text-sm">
          <span><Tag color="green">Present: {presentDays}</Tag></span>
          <span><Tag color="red">Absent: {absentDays}</Tag></span>
          <span><Tag color="gold">Half Days: {halfDays}</Tag></span>
        </div>

        <Table
          dataSource={attendance as AttendanceRecord[]}
          columns={columns}
          rowKey="id"
          size="small"
          loading={loading}
          pagination={{ pageSize: 31, showSizeChanger: false }}
        />
      </Card>
    </div>
  );
};

export default SelfServiceAttendanceView;
